// Chat view for one equipment (or the General Maritime assistant).
// History is in-memory per session; the server is stateless and receives the
// full conversation each turn. Mobile-first, light-theme design.
import { useEffect, useRef, useState } from 'react';
import { sendChatMessage, LANGUAGE_OPTIONS, ChatLimitError } from './chatApi';
import MarkdownMessage from './MarkdownMessage';
import FeedbackRow from './FeedbackRow';
import ContributionNote from './ContributionNote';
import { useBonusBanner } from './useBonusBanner';
import { goToPlans } from '../home/PlansPage';
import { useMyPlan } from '../lib/useMyPlan';
import { BRAND, PAYMENTS_VISIBLE } from '../brand';

// Admin-only testing override (the server re-verifies is_admin; for anyone
// else the param is ignored). Ids must exist in api/_lib/models.js.
const ADMIN_MODEL_OPTIONS = [
  { value: '', label: 'Model: Auto' },
  { value: 'claude-sonnet-4-5', label: 'Claude Sonnet' },
  { value: 'claude-haiku-4-5', label: 'Claude Haiku' },
  { value: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro' },
  { value: 'deepseek-v4-flash', label: 'DeepSeek V4 Flash' },
];

// Light-theme bubbles: user messages as dark navy pills on the right,
// assistant answers as soft light cards on the left. All AA:
// white on #16192b is ~15:1, #1a1f36 text on #f1f3f9 is ~13:1.
const BUBBLE_BASE = {
  fontSize: 15,
  lineHeight: 1.7,
  padding: '13px 18px',
  borderRadius: 18,
  whiteSpace: 'pre-wrap',
};
const ASSISTANT_BUBBLE = {
  ...BUBBLE_BASE,
  // Assistant answers are rendered as markdown (MarkdownMessage), which
  // handles its own line structure — pre-wrap would double the spacing.
  whiteSpace: 'normal',
  backgroundColor: 'var(--surface-3, #f1f3f9)',
  border: '1px solid var(--border, #e3e7f0)',
  color: 'var(--text, #1a1f36)',
  borderTopLeftRadius: 6,
};
const USER_BUBBLE = {
  ...BUBBLE_BASE,
  backgroundColor: 'var(--primary, #16192b)',
  border: '1px solid var(--primary, #16192b)',
  color: '#ffffff',
  borderTopRightRadius: 6,
};

// Same icon as the General Maritime Chat card and the home prompt bar, so the
// user can visually tie all three to the same assistant.
export const GENERAL_CHAT_ICON = '🌊';

const GENERAL_EXAMPLES = [
  'What does SIRE 2.0 check on mooring winches?',
  'MARPOL Annex VI — what applies to my vessel?',
  'Difference between ME-GI and X-DF?',
];
const MANUAL_EXAMPLES = [
  'High back pressure alarm right after startup',
  'Exhaust temp deviation on one unit',
  'Pre-start checks before maneuvering',
];

// "main_engine/man-b-w/me-gi" -> "MAN-B-W · ME-GI" (skip the category slug).
export function equipmentTitle(path) {
  if (!path || path === 'general') return 'General Maritime Assistant';
  const parts = String(path).split('/');
  const specific = parts.length > 1 ? parts.slice(1) : parts;
  return specific.map(p => p.toUpperCase()).join(' · ');
}

function RemainingChip({ remaining }) {
  if (!remaining) return null;
  // remaining.left sums every pool (trial + monthly + earned bonus).
  const label =
    remaining.type === 'trial'
      ? `TRIAL · ${remaining.left} LEFT`
      : remaining.type === 'bonus'
        ? `BONUS · ${remaining.left} LEFT`
        : `${remaining.left}/${remaining.limit} LEFT`;
  return (
    <span
      style={{
        fontFamily: 'var(--mono)',
        fontSize: 9.5,
        letterSpacing: 1,
        color: remaining.left <= 2 ? 'var(--yellow)' : 'var(--text-2)',
        border: '1px solid var(--border)',
        borderRadius: 999,
        padding: '5px 12px',
        whiteSpace: 'nowrap',
        background: 'var(--surface-1, #ffffff)',
      }}
    >
      {label}
    </span>
  );
}

export default function ChatView({
  equipmentPath,
  title,
  subtitle,
  icon,
  onBack,
  initialInput = '',
}) {
  const isGeneral = equipmentPath === 'general';
  const headerIcon = icon || (isGeneral ? GENERAL_CHAT_ICON : '📚');
  const headerTitle = title || equipmentTitle(equipmentPath);
  const headerSubtitle = subtitle || (isGeneral ? 'Shared maritime knowledge base' : equipmentPath);
  const examples = isGeneral ? GENERAL_EXAMPLES : MANUAL_EXAMPLES;

  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState(initialInput);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [language, setLanguage] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [modelOverride, setModelOverride] = useState('');
  const { isAdmin } = useMyPlan();
  const { banner, dismiss, syncBonus } = useBonusBanner();
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  async function send() {
    const question = input.trim();
    if (!question || sending || limitReached) return;
    const nextMessages = [...messages, { role: 'user', content: question }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    setError(null);
    try {
      const result = await sendChatMessage({
        messages: nextMessages,
        equipmentPath,
        language,
        modelOverride: isAdmin ? modelOverride : '',
      });
      setMessages(prev => [
        ...prev,
        // query_log_id ties this answer to its log row so the feedback
        // buttons under the bubble know what they are rating.
        { role: 'assistant', content: result.answer, queryLogId: result.query_log_id },
      ]);
      setRemaining(result.remaining);
      syncBonus(result.remaining?.bonus);
      if (result.remaining?.left === 0) setLimitReached(true);
    } catch (e) {
      if (e instanceof ChatLimitError) {
        setLimitReached(true);
        if (e.remaining) setRemaining(e.remaining);
      } else {
        setError(e.message || 'Something went wrong. Please retry.');
      }
      // Put the unanswered question back in the box so nothing is lost.
      setMessages(messages);
      setInput(question);
    } finally {
      setSending(false);
    }
  }

  function fillExample(text) {
    setInput(text);
    inputRef.current?.focus();
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '70vh',
        fontFamily: 'var(--sans)',
        color: 'var(--text)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap',
          paddingBottom: 14,
          borderBottom: '1px solid var(--border-soft, #eceff6)',
        }}
      >
        <button
          className="btn btn-ghost"
          onClick={onBack}
          style={{ padding: '9px 16px', fontSize: 12, minHeight: 40 }}
        >
          ←
        </button>
        <div
          aria-hidden="true"
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: 'var(--surface-3, #f1f3f9)',
            border: '1px solid var(--border, #e3e7f0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
            flexShrink: 0,
          }}
        >
          {headerIcon}
        </div>
        {/* min-width keeps the title readable on narrow phones: the remaining
            chip / selects wrap below instead of squeezing it into mid-word
            breaks; break-word overrides the dialog-inherited anywhere. */}
        <div style={{ minWidth: 'min(60%, 240px)', flex: 1, overflowWrap: 'break-word' }}>
          <div style={{ fontSize: 15.5, fontWeight: 800, wordBreak: 'break-word' }}>
            {headerTitle}
          </div>
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--text-2)',
              marginTop: 1,
              wordBreak: 'break-all',
            }}
          >
            {headerSubtitle}
          </div>
        </div>
        <RemainingChip remaining={remaining} />
        {isAdmin && (
          <select
            aria-label="Model override (admin testing)"
            value={modelOverride}
            onChange={e => setModelOverride(e.target.value)}
            style={{
              background: 'var(--surface-1, #ffffff)',
              border: '1px solid var(--amber-line, rgba(180,83,9,0.3))',
              borderRadius: 999,
              padding: '8px 10px',
              color: 'var(--amber-text, var(--amber))',
              fontSize: 11.5,
              fontFamily: 'var(--sans)',
              outline: 'none',
              minHeight: 40,
            }}
          >
            {ADMIN_MODEL_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
        <select
          aria-label="Response language"
          value={language}
          onChange={e => setLanguage(e.target.value)}
          style={{
            background: 'var(--surface-1, #ffffff)',
            border: '1px solid var(--border)',
            borderRadius: 999,
            padding: '8px 10px',
            color: 'var(--text-2)',
            fontSize: 11.5,
            fontFamily: 'var(--sans)',
            outline: 'none',
            minHeight: 40,
          }}
        >
          {LANGUAGE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {banner && (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'rgba(4,120,87,0.08)',
            border: '1px solid rgba(4,120,87,0.3)',
            borderRadius: 14,
            padding: '10px 14px',
            fontSize: 13,
            color: 'var(--green)',
            marginBottom: 12,
          }}
        >
          <span style={{ flex: 1 }}>🎉 Bonus messages added to your account — thank you!</span>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--green)',
              cursor: 'pointer',
              fontSize: 15,
              padding: '4px 6px',
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: '58vh',
          padding: '6px 2px',
          marginBottom: 14,
        }}
      >
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '36px 12px 20px' }}>
            <div
              aria-hidden="true"
              style={{
                width: 76,
                height: 76,
                borderRadius: '50%',
                background: 'var(--surface-3, #f1f3f9)',
                border: '1px solid var(--border, #e3e7f0)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 34,
                marginBottom: 16,
              }}
            >
              {headerIcon}
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 8 }}>{headerTitle}</div>
            <p
              style={{
                color: 'var(--text-2)',
                fontSize: 14,
                lineHeight: 1.65,
                maxWidth: 420,
                margin: '0 auto 18px',
              }}
            >
              {isGeneral
                ? 'Ask any marine engineering or regulatory question — answers come from the shared maritime knowledge base. No manual needed.'
                : 'Ask anything about this equipment — alarms, parameters, troubleshooting. Answers are grounded in your uploaded manual.'}
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              {examples.map(ex => (
                <button key={ex} className="chip" onClick={() => fillExample(ex)}>
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`msg ${m.role === 'assistant' ? 'insp' : 'user'}`}>
            <div className={`avatar ${m.role === 'assistant' ? 'insp' : 'you'}`}>
              {m.role === 'assistant' ? headerIcon : '👷'}
            </div>
            <div className="bubble">
              <div className="who" style={{ color: 'var(--text-2)', fontSize: 10 }}>
                {m.role === 'assistant' ? BRAND.name.toUpperCase() : 'YOU'}
              </div>
              {/* Assistant answers render as safe markdown; user messages
                  stay plain text (React escapes both — no raw HTML ever). */}
              {/* chat-bubble-body: theme-side overflow guards (long URLs wrap,
                  code blocks scroll inside the bubble) */}
              <div
                className="chat-bubble-body"
                style={m.role === 'assistant' ? ASSISTANT_BUBBLE : USER_BUBBLE}
              >
                {m.role === 'assistant' ? <MarkdownMessage text={m.content} /> : m.content}
              </div>
              {m.role === 'assistant' && <FeedbackRow queryLogId={m.queryLogId} />}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ color: 'var(--text-2)', fontSize: 13, paddingLeft: 46 }}>Thinking…</div>
        )}
      </div>

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            color: 'var(--red)',
            fontSize: 12.5,
            marginBottom: 10,
          }}
        >
          <span style={{ flex: '1 1 auto' }}>{error}</span>
          {/* The failed question is restored into the input on error, so a
              retry is just sending again — nothing to retype. */}
          <button
            className="btn btn-ghost"
            onClick={send}
            disabled={sending || !input.trim()}
            style={{ padding: '6px 14px', fontSize: 11.5, minHeight: 32 }}
          >
            ↻ Retry
          </button>
        </div>
      )}

      {limitReached ? (
        <div
          style={{
            background: 'rgba(161,98,7,0.08)',
            border: '1px solid rgba(161,98,7,0.3)',
            borderRadius: 14,
            padding: '14px 16px',
            fontSize: 13.5,
            lineHeight: 1.6,
            color: 'var(--yellow)',
          }}
        >
          You're out of messages for this month. Share a note from your experience — approved notes
          earn +20 messages.{PAYMENTS_VISIBLE ? ' Or upgrade for more.' : ''}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              className="btn"
              onClick={() => setNoteOpen(true)}
              style={{
                padding: '8px 16px',
                fontSize: 12,
                background: 'var(--primary, #16192b)',
                color: '#ffffff',
              }}
            >
              ✍️ Add a note
            </button>
            {PAYMENTS_VISIBLE && (
              <button
                className="btn btn-ghost"
                onClick={goToPlans}
                style={{ padding: '8px 16px', fontSize: 12 }}
              >
                See plans
              </button>
            )}
          </div>
        </div>
      ) : (
        <form
          className="prompt-bar"
          onSubmit={e => {
            e.preventDefault();
            send();
          }}
        >
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Type your question…"
            disabled={sending}
            aria-label="Chat message"
          />
          <button
            type="submit"
            className="prompt-send"
            disabled={sending || !input.trim()}
            aria-label="Send"
            style={sending || !input.trim() ? { opacity: 0.5 } : undefined}
          >
            ➤
          </button>
        </form>
      )}

      <ContributionNote equipmentPath={equipmentPath} open={noteOpen} onOpenChange={setNoteOpen} />

      <div
        style={{
          textAlign: 'center',
          fontSize: 11.5,
          color: 'var(--text-2)',
          marginTop: 12,
          lineHeight: 1.5,
        }}
      >
        ⚠ AI assistance — always verify with official documentation and company procedures.
      </div>
    </div>
  );
}
